import { SystemEntity } from '@erp/shared-types';
import { cn } from '@/lib/utils';
import { useOutboundEmail } from '@/api/rpc/hooks/communications/useOutboundEmail';
import { EnvelopeIcon } from '@phosphor-icons/react';
import { format } from 'date-fns';

interface OutboundEmailPreviewProps {
  entityType: SystemEntity; // Required by preview component registry but not used - type is always OUTBOUND_EMAIL
  entityId: string;
  className?: string;
}

export function OutboundEmailPreview({
  entityId,
  className,
}: OutboundEmailPreviewProps) {
  const { outboundEmail, isLoading, error } = useOutboundEmail(entityId);

  if (isLoading) {
    return (
      <div className={cn('min-w-64 max-w-sm p-3', className)}>
        <div className="flex items-center gap-2 mb-2">
          <EnvelopeIcon className="size-4 text-muted-foreground" />
          <h4 className="text-sm font-semibold">Remittance Email</h4>
        </div>
        <p className="text-xs text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (error || !outboundEmail) {
    return (
      <div className={cn('min-w-64 max-w-sm p-3', className)}>
        <div className="flex items-center gap-2 mb-2">
          <EnvelopeIcon className="size-4 text-muted-foreground" />
          <h4 className="text-sm font-semibold">Remittance Email</h4>
        </div>
        <p className="text-xs text-muted-foreground">
          Unable to load email details
        </p>
      </div>
    );
  }

  return (
    <div className={cn('min-w-64 max-w-sm p-3', className)}>
      <div className="flex items-center gap-2 mb-3">
        <EnvelopeIcon className="size-4 text-muted-foreground" />
        <h4 className="text-sm font-semibold truncate">
          {outboundEmail.subject}
        </h4>
      </div>

      <div className="space-y-2 text-xs w-full">
        <div className="flex items-center gap-1">
          <span className="text-muted-foreground">To: </span>
          <span className="font-medium truncate block">{outboundEmail.to}</span>
        </div>

        {outboundEmail.cc && (
          <div>
            <span className="text-muted-foreground">CC: </span>
            <span className="font-medium truncate block">
              {outboundEmail.cc}
            </span>
          </div>
        )}

        {outboundEmail.sentAt && (
          <div className="flex items-center gap-1">
            <span className="text-muted-foreground">Sent: </span>
            <span className="font-medium text-xs">
              {format(new Date(outboundEmail.sentAt), 'MMM d, yyyy, h:mm a')}
            </span>
          </div>
        )}

        {outboundEmail.failureReason && (
          <div className="flex items-center gap-1">
            <span className="text-muted-foreground">Error: </span>
            <span className="font-medium text-destructive text-xs">
              {outboundEmail.failureReason}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
